/** Spending limits in one place (ADR-0063): Administration › Spending limits, approval amounts, ROI. */
export default {
  en: {
    tab: { overview: "Overview", spending: "Spending limits" },
    intro:
      "One place for every spending limit. Pick what a limit covers, how much it may spend, and what happens when it is reached. Model costs are counted as they happen, for every agent and team.",
    perRequestIntro: "Each agent also has its own limit for a single request. It is part of the agent's design and is changed in the builder.",
    add: "Add a spending limit",
    edit: "Change limit for {{name}}",
    delete: "Delete limit for {{name}}",
    deleteConfirm: "Delete this spending limit? Requests will no longer be checked against it.",
    saved: "Spending limit saved",
    deleted: "Spending limit deleted",
    none: "No spending limits yet. Without a limit, agents can keep spending.",
    readOnly: "You can see the limits. Finance and admins can change them.",
    scope: { tenant: "The whole organisation", team: "Team", project: "Project", agent: "Agent" },
    scopeShort: { tenant: "Whole organisation", team: "A team", project: "A project", agent: "An agent" },
    period: { day: "per day", month: "per month" },
    periodNoun: { day: "day", month: "month" },
    sentence: {
      cost: "{{who}} may spend up to {{amount}} {{period}}.",
      tokens: "{{who}} may use up to {{amount}} tokens {{period}}.",
      warn: "Warn at {{pct}}%. At the limit, requests keep running (warning only).",
      stop: "Warn at {{pct}}%. At the limit, new requests stop until the next {{period}}.",
      approval: "Warn at {{pct}}%. At the limit, new requests stop and an approver can allow more.",
    },
    used: "Used {{used}} of {{limit}} · {{remaining}} left",
    extra: "Includes {{extra}} more allowed for this {{period}}.",
    forecast: "At this pace: about {{total}} by the end of the {{period}}.",
    runsOut: "Expected to reach the limit around {{date}}.",
    state: { ok: "On track", soft: "Close to the limit", exhausted: "Limit reached" },
    allowMore: "Allow more this {{period}}",
    allowMoreTitle: "Allow more spending for {{name}}",
    allowMoreHelp: "Adds to this {{period}} only. The normal limit comes back next {{period}}.",
    allowAmount: "Extra amount",
    allowReason: "Why is it needed?",
    allowReasonHelp: "Kept in the audit trail.",
    allowed: "Extra spending allowed",
    wizard: {
      titleNew: "Add a spending limit",
      titleEdit: "Change spending limit",
      what: "1. What does this limit cover?",
      pick: { team: "Choose a team", project: "Choose a project", agent: "Choose an agent" },
      pickHelp: "Pick from the list or type a new name.",
      noUse: "No use recorded yet",
      monthUse: "{{amount}} this month",
      howMuch: "2. How much may it spend?",
      amount: "Amount",
      inTokens: "Count tokens instead of money (advanced)",
      tokens: "Tokens",
      periodLabel: "Every",
      happens: "3. What happens at the limit?",
      warnAt: "Warn me when {{pct}}% is used",
      onLimit: {
        warn: "Only warn — requests keep running",
        stop: "Stop new requests until the next {{period}}",
        approval: "Stop and ask an approver — Finance or an admin can allow more",
      },
      preview: "Preview",
      soFar: "So far this {{period}}: {{amount}}.",
      atPace: "At this pace: about {{amount}} by the end of the {{period}} ({{pct}} of the limit).",
      noData: "No use recorded yet for this choice.",
      converted: "The meter counts in {{meter}}: {{amount}}.",
      save: "Save limit",
      cancel: "Cancel",
    },
    issues: {
      subject_required: "Choose who this limit covers.",
      limit_invalid: "Enter an amount above zero.",
      soft_pct_invalid: "The warning level must be between 1% and 100%.",
      scope_value_invalid: "Names may use letters, digits and . _ : @ / - only.",
    },
    perRequest: {
      title: "Limits per request (set in each agent)",
      agent: "Agent",
      owner: "Owner",
      cost: "Most a single request may cost",
      tokens: "Most text one request may handle (units)",
      steps: "Most steps one request may take",
      change: "Change in the builder",
      none: "No agents in the catalogue yet.",
    },
    approvals: {
      tab: "Approval amounts",
      intro:
        "When an agent wants to do something with a money amount (for example pay an invoice), a person must approve it if the amount is above the limit for the agent's risk level. You can make these limits stricter than the platform's, never looser.",
      tier: "Risk level {{tier}}",
      tierHelp: { "1": "Low risk", "2": "Medium risk", "3": "High risk", "4": "Critical" },
      platform: "Platform limit",
      platformNone: "No platform limit",
      yours: "Your limit",
      useplatform: "Same as platform",
      confidence: "Ask for approval when the agent is less sure than",
      confidenceHelp: "How sure the agent says it is, from 0% to 100%.",
      always: "Risk level {{tier}} and above always needs approval for actions that change something.",
      previewTitle: "Try it",
      previewAmount: "An agent wants to pay",
      previewTier: "Risk level",
      needs: "Needs approval by a person with the Approver role.",
      needsWhy: { amount: "The amount is above {{limit}}.", tier: "Risk level {{tier}} always needs approval." },
      noNeed: "No approval needed for the amount.",
      currency: "Your currency",
      currencyHelp: "Used for approval amounts, spending limits and ROI.",
      usdRate: "1 US dollar =",
      usdRateHelp: "Model prices are counted in US dollars; this rate shows them in your currency.",
      save: "Save approval amounts",
      saved: "Approval amounts saved",
      tooHigh: "Must not be above the platform limit ({{limit}}).",
      tooLow: "Must not be below the platform level ({{limit}}).",
    },
    platformApprovals: {
      intro: "Platform-wide approval amounts. Each organisation may make these stricter for itself. Amounts are in each organisation's own currency.",
      amount: "Ask for approval above",
      confidence: "…or when the agent is less sure than",
      minTier: "Always ask for approval from risk level",
    },
    roi: {
      title: "How ROI is worked out",
      body: "ROI = hours saved × cost of one hour of a person's time ÷ model cost.",
      hourly: "One hour of a person's time costs",
      source: { tenant: "Set by your organisation", platform_default: "Platform default — set your own" },
      edit: "Change",
      save: "Save",
      saved: "ROI assumption saved",
      period: "Period",
      periods: { "7d": "Last 7 days", "30d": "Last 30 days", "90d": "Last 90 days" },
    },
    alerts: {
      todo: "What to do: {{text}}",
      do: {
        budget_soft_limit: "Check which agent or team is spending most; raise the limit or slow down use before it is reached.",
        budget_exhausted: "New requests may be stopped. Raise the limit in Spending limits, or wait for the next period.",
        budget_needs_approval: "An approver (Finance or an admin) should open Spending limits and allow more, or keep it stopped.",
        anomaly: "Use today is much higher than usual. Check the agent's recent runs for loops or unexpected traffic.",
        BudgetSoftThreshold: "Check spending in Spending limits and decide whether to raise the limit.",
        ApprovalSlaBreached: "Open the Inbox and decide the waiting approval, or reassign it.",
        CanaryPaused: "A new version is paused for checks. Release owners review it in Governance › Releases.",
        default: "Open the linked screen and check recent changes; ask the platform operator if unsure.",
      },
    },
  },
  ar: {
    tab: { overview: "نظرة عامة", spending: "حدود الإنفاق" },
    intro:
      "مكان واحد لكل حدود الإنفاق. اختر ما يشمله الحد، وكم يمكن إنفاقه، وما يحدث عند بلوغه. تُحتسب تكاليف النماذج لحظة حدوثها لكل وكيل وفريق.",
    perRequestIntro: "لكل وكيل أيضًا حد خاص بالطلب الواحد. وهو جزء من تصميم الوكيل ويُغيَّر من المُنشئ.",
    add: "إضافة حد إنفاق",
    edit: "تغيير حد {{name}}",
    delete: "حذف حد {{name}}",
    deleteConfirm: "حذف حد الإنفاق هذا؟ لن تُفحص الطلبات مقابله بعد الآن.",
    saved: "حُفظ حد الإنفاق",
    deleted: "حُذف حد الإنفاق",
    none: "لا توجد حدود إنفاق بعد. بدون حد يمكن للوكلاء الاستمرار في الإنفاق.",
    readOnly: "يمكنك رؤية الحدود. يستطيع فريق المالية والمسؤولون تغييرها.",
    scope: { tenant: "الجهة بأكملها", team: "فريق", project: "مشروع", agent: "وكيل" },
    scopeShort: { tenant: "الجهة بأكملها", team: "فريق", project: "مشروع", agent: "وكيل" },
    period: { day: "يوميًا", month: "شهريًا" },
    periodNoun: { day: "اليوم", month: "الشهر" },
    sentence: {
      cost: "{{who}}: إنفاق حتى {{amount}} {{period}}.",
      tokens: "{{who}}: استخدام حتى {{amount}} وحدة نصية {{period}}.",
      warn: "تنبيه عند {{pct}}%. عند بلوغ الحد تستمر الطلبات (تنبيه فقط).",
      stop: "تنبيه عند {{pct}}%. عند بلوغ الحد تتوقف الطلبات الجديدة حتى {{period}} التالي.",
      approval: "تنبيه عند {{pct}}%. عند بلوغ الحد تتوقف الطلبات الجديدة ويمكن لمعتمِد السماح بالمزيد.",
    },
    used: "المستخدم {{used}} من {{limit}} · المتبقي {{remaining}}",
    extra: "يشمل {{extra}} إضافية مسموحًا بها لهذا {{period}}.",
    forecast: "بهذا المعدل: نحو {{total}} بنهاية {{period}}.",
    runsOut: "يُتوقع بلوغ الحد نحو {{date}}.",
    state: { ok: "ضمن المسار", soft: "قريب من الحد", exhausted: "بلغ الحد" },
    allowMore: "السماح بالمزيد لهذا {{period}}",
    allowMoreTitle: "السماح بإنفاق إضافي: {{name}}",
    allowMoreHelp: "يُضاف لهذا {{period}} فقط. يعود الحد المعتاد في {{period}} التالي.",
    allowAmount: "المبلغ الإضافي",
    allowReason: "لماذا يلزم؟",
    allowReasonHelp: "يُحفظ في سجل التدقيق.",
    allowed: "سُمح بالإنفاق الإضافي",
    wizard: {
      titleNew: "إضافة حد إنفاق",
      titleEdit: "تغيير حد الإنفاق",
      what: "١. ما الذي يشمله هذا الحد؟",
      pick: { team: "اختر فريقًا", project: "اختر مشروعًا", agent: "اختر وكيلًا" },
      pickHelp: "اختر من القائمة أو اكتب اسمًا جديدًا.",
      noUse: "لا يوجد استخدام مسجل بعد",
      monthUse: "{{amount}} هذا الشهر",
      howMuch: "٢. كم يمكن إنفاقه؟",
      amount: "المبلغ",
      inTokens: "احتساب الوحدات النصية بدل المال (متقدم)",
      tokens: "الوحدات النصية",
      periodLabel: "كل",
      happens: "٣. ماذا يحدث عند بلوغ الحد؟",
      warnAt: "نبّهني عند استخدام {{pct}}%",
      onLimit: {
        warn: "تنبيه فقط — تستمر الطلبات",
        stop: "إيقاف الطلبات الجديدة حتى {{period}} التالي",
        approval: "الإيقاف وطلب معتمِد — يمكن للمالية أو المسؤول السماح بالمزيد",
      },
      preview: "معاينة",
      soFar: "حتى الآن هذا {{period}}: {{amount}}.",
      atPace: "بهذا المعدل: نحو {{amount}} بنهاية {{period}} ({{pct}} من الحد).",
      noData: "لا يوجد استخدام مسجل لهذا الاختيار بعد.",
      converted: "يحتسب العداد بعملة {{meter}}: {{amount}}.",
      save: "حفظ الحد",
      cancel: "إلغاء",
    },
    issues: {
      subject_required: "اختر من يشمله هذا الحد.",
      limit_invalid: "أدخل مبلغًا أكبر من صفر.",
      soft_pct_invalid: "يجب أن يكون مستوى التنبيه بين 1% و100%.",
      scope_value_invalid: "يمكن أن تحتوي الأسماء على حروف وأرقام و . _ : @ / - فقط.",
    },
    perRequest: {
      title: "حدود الطلب الواحد (تُضبط في كل وكيل)",
      agent: "الوكيل",
      owner: "المالك",
      cost: "أقصى تكلفة لطلب واحد",
      tokens: "أقصى وحدات نصية لكل طلب",
      steps: "أقصى عدد خطوات للطلب الواحد",
      change: "التغيير في المُنشئ",
      none: "لا يوجد وكلاء في الدليل بعد.",
    },
    approvals: {
      tab: "مبالغ الاعتماد",
      intro:
        "عندما يريد وكيل تنفيذ إجراء بمبلغ مالي (مثل دفع فاتورة) يجب أن يعتمده شخص إذا تجاوز المبلغ حد مستوى خطورة الوكيل. يمكنك جعل هذه الحدود أشد من حدود المنصة، لا أخف.",
      tier: "مستوى الخطورة {{tier}}",
      tierHelp: { "1": "خطورة منخفضة", "2": "خطورة متوسطة", "3": "خطورة عالية", "4": "حرج" },
      platform: "حد المنصة",
      platformNone: "لا يوجد حد للمنصة",
      yours: "حدّكم",
      useplatform: "مثل المنصة",
      confidence: "اطلب الاعتماد عندما تقل ثقة الوكيل عن",
      confidenceHelp: "مدى تأكد الوكيل، من 0% إلى 100%.",
      always: "مستوى الخطورة {{tier}} وما فوقه يتطلب الاعتماد دائمًا للإجراءات التي تُغيّر شيئًا.",
      previewTitle: "جرّب",
      previewAmount: "يريد وكيل دفع",
      previewTier: "مستوى الخطورة",
      needs: "يحتاج اعتمادًا من شخص لديه دور المعتمِد.",
      needsWhy: { amount: "المبلغ أعلى من {{limit}}.", tier: "مستوى الخطورة {{tier}} يتطلب الاعتماد دائمًا." },
      noNeed: "لا يلزم اعتماد لهذا المبلغ.",
      currency: "عملتكم",
      currencyHelp: "تُستخدم لمبالغ الاعتماد وحدود الإنفاق والعائد على الاستثمار.",
      usdRate: "دولار أمريكي واحد =",
      usdRateHelp: "تُحتسب أسعار النماذج بالدولار الأمريكي؛ يعرضها هذا السعر بعملتكم.",
      save: "حفظ مبالغ الاعتماد",
      saved: "حُفظت مبالغ الاعتماد",
      tooHigh: "يجب ألا يتجاوز حد المنصة ({{limit}}).",
      tooLow: "يجب ألا يقل عن مستوى المنصة ({{limit}}).",
    },
    platformApprovals: {
      intro: "مبالغ الاعتماد على مستوى المنصة. يمكن لكل جهة جعلها أشد لنفسها. المبالغ بعملة كل جهة.",
      amount: "اطلب الاعتماد فوق",
      confidence: "…أو عندما تقل ثقة الوكيل عن",
      minTier: "اطلب الاعتماد دائمًا ابتداءً من مستوى الخطورة",
    },
    roi: {
      title: "كيف يُحسب العائد على الاستثمار",
      body: "العائد = الساعات الموفَّرة × تكلفة ساعة من وقت الموظف ÷ تكلفة النماذج.",
      hourly: "تكلفة ساعة من وقت الموظف",
      source: { tenant: "حددتها جهتكم", platform_default: "القيمة الافتراضية للمنصة — حدّد قيمتكم" },
      edit: "تغيير",
      save: "حفظ",
      saved: "حُفظ افتراض العائد",
      period: "الفترة",
      periods: { "7d": "آخر 7 أيام", "30d": "آخر 30 يومًا", "90d": "آخر 90 يومًا" },
    },
    alerts: {
      todo: "ما العمل: {{text}}",
      do: {
        budget_soft_limit: "تحقق من الوكيل أو الفريق الأعلى إنفاقًا؛ ارفع الحد أو خفّف الاستخدام قبل بلوغه.",
        budget_exhausted: "قد تتوقف الطلبات الجديدة. ارفع الحد من حدود الإنفاق أو انتظر الفترة التالية.",
        budget_needs_approval: "على المعتمِد (المالية أو المسؤول) فتح حدود الإنفاق والسماح بالمزيد أو إبقاء الإيقاف.",
        anomaly: "استخدام اليوم أعلى بكثير من المعتاد. راجع آخر تشغيلات الوكيل بحثًا عن تكرار أو حركة غير متوقعة.",
        BudgetSoftThreshold: "راجع الإنفاق في حدود الإنفاق وقرر ما إذا كان يلزم رفع الحد.",
        ApprovalSlaBreached: "افتح صندوق المهام واتخذ القرار في الاعتماد المنتظر أو أعد إسناده.",
        CanaryPaused: "إصدار جديد متوقف للفحص. يراجعه مالكو الإصدار في الحوكمة › الإصدارات.",
        default: "افتح الشاشة المرتبطة وراجع التغييرات الأخيرة؛ واسأل مشغّل المنصة عند الشك.",
      },
    },
  },
};
